import { LayoutsE, ModuleE, PathsE, PathsParams } from "../../routes";
import { typedInfiniteQuery, typedMutation } from "../types/typedQueries";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { bareInstance, camelizeResponse } from "./common";
import {
  BlackListDTO,
  BlackListT,
  CreateBlackListItem,
  DeleteBlackListItem,
} from "../types/blacklist";
import { PaginationT } from "../types/services";
import useToastes from "../../hooks/useToastes";
import useTypedHistory from "../../hooks/useTypedHistory";

interface IProps {
  domain: string;
  token: string;
  module?: ModuleE;
}

export const useBlackListService = ({ domain, token, module }: IProps) => {
  return {
    useGetBlackList: typedInfiniteQuery<BlackListT>(({ params }) => {
      return useInfiniteQuery({
        queryKey: [ModuleE.BLACKLIST, token, domain, params],
        queryFn: () =>
          bareInstance.get<
            unknown,
            BlackListT
          >(`${domain}/${module}/`, {
            headers: { "x-service-token": token },
            params,
          }),
        initialPageParam: 1,
        getNextPageParam: () => null,
        getPreviousPageParam: () => null,
        select: (data) => {
          const blackListData = data as unknown as { pages: { totalCount: number; entries: BlackListDTO[], pagination: PaginationT}[]; pageParams: number[]}
          return  {
            ...blackListData,
            pages: blackListData.pages.map((page) => {
              const pagination = camelizeResponse(page.pagination) as PaginationT;
              return {
                data: page.entries.map((i) => ({
                  id: i.key,
                  key: i.key,
                  telegram_id: i.telegram_id,
                })),
                page: pagination?.currentPage,
                size: pagination?.perPage,
                total: page.totalCount,
              };
            }),
          };
        },
      });
    }),


    useCreateBlackListItem: typedMutation<CreateBlackListItem>((params) => {
      const serviceId = params?.pathParams?.serviceId;
      const { successToast } = useToastes();
      const queryCache = useQueryClient();
      const push = useTypedHistory<PathsParams[PathsE.CREATE_BLACKLIST_ITEM]>();

      return useMutation({
        mutationFn: (data) =>
          bareInstance.post(`${domain}/${module}/add`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("New item was successfully added to black list");
          queryCache.invalidateQueries({ queryKey: [ModuleE.BLACKLIST] });
          push(PathsE.BLACKLIST, LayoutsE.DASHBOARD, { serviceId } );
        },
      });
    }),

    useDeleteBlackListItem: typedMutation<DeleteBlackListItem>(() => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();

      return useMutation({
        mutationFn: (data) =>
          bareInstance.delete(
            `${domain}/${module}/remove`,
            { headers: { "x-service-token": token }, data },
          ),
        onSuccess: () => {
          successToast("Item was successfully deleted");
          queryCache.invalidateQueries({ queryKey: [ModuleE.BLACKLIST] });
        },
      });
    }),
  };
};
